import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArchiveIcon, CheckIcon } from 'lucide-react';
import {
  Alert,
  DataGrid,
  FilterBar,
  ActionBar,
  Skeleton,
  MetricCard,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
  ActionDefinition,
  ColumnDef,
} from '@aaimplatform/ui';
import { useActiveLoans } from '../hooks/useActiveLoans';
import type { AdminPortalBff } from '@aaimplatform/institutional-types';
import { loansApi, type BulkLoanActionResponse, type LoanArchiveRefusal } from '../api/services/loans';
import { ApiError } from '../api/client';
import { readErrorCopy } from '../api/error-message';
import { collateralLabel, loanStatusLabel } from '../api/loan-labels';
import { CHA_TIER_CLASSES } from './collateral-policy/chaTier';
import { ChaTierBadge } from './pledges/pledge-display';

type Loan = AdminPortalBff.components['schemas']['LoanViewItem'];
type ArchiveRefusalReason = AdminPortalBff.components['schemas']['LoanArchiveRefusal']['refused'][number]['reason'];

/** The query key root useActiveLoans reads under, invalidated after a bulk write. */
const ACTIVE_LOANS_KEY = ['admin', 'loans', 'active'] as const;

const LOAD_FAILED_COPY = 'Loans could not be loaded. Retry, or contact support if it persists.';
const APPROVE_FAILED_COPY = 'The approval request failed. No loans were changed by this page.';
const CLOSE_FAILED_COPY = 'The close request failed. No loans were changed by this page.';

/** Compact dollars, three significant digits: $80,000 reads $80K, never $0.1M. */
const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumSignificantDigits: 3,
});

/**
 * An absent collateral value or LTV (LoanViewItem omits it for a loan with no
 * package, a package never valued, or no stored ratio) prints this, never $0
 * or 0% (ruling absence-representable-contracts).
 */
const NOT_REPORTED = 'Not reported';

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count !== 1 ? 's' : ''}`;

/**
 * The grid bucket names (LoanViewItem.status). The Active bucket holds ACTIVE
 * and FUNDED loans and reads 'Active and funded' wherever the page names it
 * (ruling active-loans-tile-vs-bucket (a)); the Dashboard's 'Active Loans'
 * tile counts ACTIVE alone. Review holds DRAFT, SUBMITTED, UNDERWRITING and
 * APPROVED, and Alert holds SUSPENDED and DEFAULTED (ruling
 * loan-status-buckets-archive-guard (a)). The Alert bucket reads 'Suspended or
 * defaulted' (operator ruling 2026-09-29): 'Alert' on this page names a
 * Collateral Health Alert, which the fourth card counts by CHA tier.
 */
const STATUS_LABELS: Record<Loan['status'], string> = {
  Active: 'Active and funded',
  Review: 'Review',
  Alert: 'Suspended or defaulted',
};

/** The loans Close loan accepts, named in its confirmation (ruling loan-status-buckets-archive-guard (a)). */
const CLOSE_RULE = 'Only draft, submitted or underwriting loans with no active pledge can close.';

/** One line per refusal reason, keyed by the generated enum so a new reason fails tsc until it has copy. */
const REFUSAL_REASON_COPY: Record<ArchiveRefusalReason, (loanStatus: string | undefined) => string> = {
  STATUS_NOT_CLOSABLE: (loanStatus) =>
    loanStatus ? `${loanStatusLabel(loanStatus)} loans may not close` : 'its status may not close',
  ACTIVE_PLEDGE: () => 'an active pledge backs this loan',
};

/** A refusal entry as the contract declares it: a string id, a declared reason, and a string status when present. */
function isRefusalEntry(entry: unknown): entry is LoanArchiveRefusal['refused'][number] {
  if (!entry || typeof entry !== 'object') return false;
  const { id, reason, loanStatus } = entry as { id?: unknown; reason?: unknown; loanStatus?: unknown };
  return (
    typeof id === 'string' &&
    typeof reason === 'string' &&
    Object.prototype.hasOwnProperty.call(REFUSAL_REASON_COPY, reason) &&
    (loanStatus === undefined || typeof loanStatus === 'string')
  );
}

/**
 * The loans a Close loan 409 refused (LoanArchiveRefusal.refused), or null for
 * any other failure. An entry the contract does not describe is dropped rather
 * than guessed at; a body with none left is read as any other failure, through
 * the server's message.
 */
function refusedLoansOf(err: unknown): LoanArchiveRefusal['refused'] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const refused = (err.data as { refused?: unknown } | null | undefined)?.refused;
  if (!Array.isArray(refused)) return null;
  const readable = refused.filter(isRefusalEntry);
  return readable.length > 0 ? readable : null;
}

/**
 * One line per refused loan, named as the grid names it. refused[].id is
 * Loan.id, which the rows carry as loanId; the grid's row id prefers the loan
 * number. A refused id the page does not hold prints as received.
 */
function refusalLines(refused: LoanArchiveRefusal['refused'], rows: Loan[]): Array<{ key: string; text: string }> {
  return refused.map((entry) => {
    const name = rows.find((row) => row.loanId === entry.id)?.id ?? entry.id;
    return { key: entry.id, text: `${name}: ${REFUSAL_REASON_COPY[entry.reason](entry.loanStatus)}` };
  });
}

// ---------------------------------------------------------------------------
// Maturity date range (r3-fi-g7-6)
// ---------------------------------------------------------------------------

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

/**
 * The inclusive span a FilterBar daterange value names: one of its presets,
 * or a custom 'YYYY-MM-DD to YYYY-MM-DD'. Bounds are local days, the same
 * reading the Maturity column prints. null for a value it cannot read.
 */
export function maturityRangeBounds(value: string, now: Date = new Date()): { from: Date; to: Date } | null {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (value) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'last_7_days':
      return { from: startOfDay(new Date(y, m, now.getDate() - 6)), to: endOfDay(now) };
    case 'last_30_days':
      return { from: startOfDay(new Date(y, m, now.getDate() - 29)), to: endOfDay(now) };
    case 'this_month':
      return { from: new Date(y, m, 1), to: endOfDay(new Date(y, m + 1, 0)) };
    case 'last_month':
      return { from: new Date(y, m - 1, 1), to: endOfDay(new Date(y, m, 0)) };
    case 'this_quarter': {
      const q = Math.floor(m / 3) * 3;
      return { from: new Date(y, q, 1), to: endOfDay(new Date(y, q + 3, 0)) };
    }
    case 'this_year':
      return { from: new Date(y, 0, 1), to: endOfDay(new Date(y, 11, 31)) };
    default: {
      const custom = /^(\d{4})-(\d{2})-(\d{2}) to (\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (!custom) return null;
      const [, fy, fm, fd, ty, tm, td] = custom.map(Number);
      return { from: new Date(fy, fm - 1, fd), to: endOfDay(new Date(ty, tm - 1, td)) };
    }
  }
}

/**
 * Whether a loan's maturity falls inside the chosen span. A loan with no
 * maturity date is outside every span: an absent date matches no range. An
 * unreadable span keeps nothing, so a filter chip never sits over rows it
 * did not test.
 */
export function maturityInRange(maturityDate: string | undefined, value: string, now?: Date): boolean {
  if (!maturityDate) return false;
  const bounds = maturityRangeBounds(value, now);
  if (!bounds) return false;
  const at = new Date(maturityDate);
  if (Number.isNaN(at.getTime())) return false;
  return at >= bounds.from && at <= bounds.to;
}

// ---------------------------------------------------------------------------
// Column Definitions
// ---------------------------------------------------------------------------

function getStatusColor(status: Loan['status']): string {
  switch (status) {
    case 'Active':
      return 'bg-growth/10 text-growth';
    case 'Review':
      return 'bg-warning/10 text-warning';
    case 'Alert':
      return 'bg-error/10 text-error';
    default:
      return 'bg-text-quaternary/10 text-quaternary';
  }
}

const COLUMNS: ColumnDef<Loan, unknown>[] = [
  {
    accessorKey: 'id',
    header: 'Loan ID',
    // Plain text: the row opens no detail view, so it takes no link color.
    cell: ({ getValue }) => (
      <span className="text-sm font-mono font-medium text-primary leading-normal">
        {getValue() as string}
      </span>
    ),
    size: 140,
  },
  {
    accessorKey: 'borrower',
    header: 'Borrower',
    cell: ({ getValue }) => (
      <span className="text-sm font-sans font-medium text-primary leading-normal">
        {getValue() as string}
      </span>
    ),
    size: 220,
  },
  {
    accessorKey: 'collateralType',
    header: 'Collateral',
    cell: ({ getValue }) => (
      <span className="text-sm font-sans text-secondary leading-normal">
        {collateralLabel(getValue() as string | undefined)}
      </span>
    ),
    size: 160,
  },
  {
    accessorKey: 'loanAmount',
    header: 'Loan Amount',
    cell: ({ getValue }) => {
      const val = getValue() as number | undefined;
      return (
        <span className="text-sm font-mono text-primary tabular-nums leading-normal">
          {val != null ? `$${val.toLocaleString()}` : '--'}
        </span>
      );
    },
    size: 150,
  },
  {
    accessorKey: 'collateralValue',
    // LoanViewItem.collateralValue is the package's total MARKET value. The
    // LTV divides by the eligible value in the next column (ruling
    // ltv-basis-visible), so the two headers name their bases.
    header: 'Market Value',
    cell: ({ getValue }) => {
      const val = getValue() as number | undefined;
      return val != null ? (
        <span className="text-sm font-mono text-primary tabular-nums leading-normal">
          ${val.toLocaleString()}
        </span>
      ) : (
        <span className="text-sm font-sans text-tertiary leading-normal">{NOT_REPORTED}</span>
      );
    },
    size: 160,
  },
  {
    accessorKey: 'eligibleValue',
    // LoanViewItem.eligibleValue is CollateralPackage.totalEligibleValue, the
    // denominator of the LTV beside it.
    header: 'Eligible Value',
    cell: ({ getValue }) => {
      const val = getValue() as number | undefined;
      return val != null ? (
        <span className="text-sm font-mono text-primary tabular-nums leading-normal">
          ${val.toLocaleString()}
        </span>
      ) : (
        <span className="text-sm font-sans text-tertiary leading-normal">{NOT_REPORTED}</span>
      );
    },
    size: 160,
  },
  {
    accessorKey: 'ltv',
    header: 'LTV',
    // Colored by the loan's CHA tier (LoanViewItem.tier), the monitor's verdict
    // against the institution's own policy thresholds, with the tier named
    // beside the figure so the color is never the only signal. A loan with no
    // open collateral alert carries no tier and stays neutral. Fixed 60/70
    // cutoffs once painted an LTV red that its policy may call GREEN; the cell
    // never derives a color from the figure itself.
    cell: ({ row }) => {
      const { ltv, tier } = row.original;
      const tone = tier ? CHA_TIER_CLASSES[tier].text : 'text-primary';
      return (
        <span className="inline-flex items-center gap-2">
          {ltv != null ? (
            <span className={`text-sm font-mono font-semibold ${tone} tabular-nums leading-normal`}>
              {`${ltv}%`}
            </span>
          ) : (
            <span className="text-sm font-sans text-tertiary leading-normal">{NOT_REPORTED}</span>
          )}
          {tier && <ChaTierBadge tier={tier} />}
        </span>
      );
    },
    size: 160,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue() as Loan['status'];
      return (
        <span className={`inline-flex px-3 py-1 text-xs font-sans font-semibold rounded-sm leading-normal whitespace-nowrap ${getStatusColor(status)}`}>
          {STATUS_LABELS[status] ?? status}
        </span>
      );
    },
    // Wide enough for 'Suspended or defaulted' on one line.
    size: 190,
  },
  {
    accessorKey: 'maturityDate',
    header: 'Maturity',
    cell: ({ getValue }) => {
      // maturityDate is optional post the LPM-2 projection-honesty fix:
      // a Loan row without a maturity renders '--' instead of a
      // server-fabricated current date.
      const value = getValue() as string | undefined;
      return (
        <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
          {value
            ? new Date(value).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : '--'}
        </span>
      );
    },
    size: 130,
  },
];

// ---------------------------------------------------------------------------
// Filter Definitions
// ---------------------------------------------------------------------------

/**
 * The collateral-type options are the distinct values in the loaded rows, so
 * every option can match a row. The prior list (Bitcoin, Ethereum, Pre-IPO
 * Equity, Private Equity) matched none of the values the endpoint returns.
 */
function buildFilterDefinitions(loans: Loan[]): FilterDefinition[] {
  const collateralTypes = Array.from(new Set(loans.map((l) => l.collateralType).filter(Boolean))).sort();
  return [
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Active', label: STATUS_LABELS.Active },
        { value: 'Review', label: STATUS_LABELS.Review },
        { value: 'Alert', label: STATUS_LABELS.Alert },
      ],
    },
    {
      id: 'collateralType',
      label: 'Collateral Type',
      type: 'select',
      options: collateralTypes.map((code) => ({ value: code, label: collateralLabel(code) })),
    },
    {
      id: 'maturityDate',
      label: 'Maturity Date',
      type: 'daterange',
    },
    {
      id: 'borrower',
      label: 'Borrower',
      type: 'text',
      placeholder: 'Search borrower name...',
    },
  ];
}

// ---------------------------------------------------------------------------
// ActiveLoansPage Component
// ---------------------------------------------------------------------------

type ActionResultState = {
  type: 'success' | 'warning' | 'error';
  message: string;
  /** The loans a Close loan request was refused for, one line each. */
  refused?: Array<{ key: string; text: string }>;
};

/**
 * Active Loans Page -- Modernized with DataGrid, FilterBar, and ActionBar
 *
 * Replaces the legacy HTML table with the UI Kit Phase 0 DataGrid component.
 * Adds FilterBar for search and structured filtering, and ActionBar for
 * bulk operations on selected loans. Export is the grid's own toolbar export.
 */
export function ActiveLoansPage() {
  const queryClient = useQueryClient();
  const { data: response, isLoading, error } = useActiveLoans();
  const loans = useMemo(() => response?.data ?? [], [response]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [selectedRows, setSelectedRows] = useState<Loan[]>([]);
  const [actionPending, setActionPending] = useState(false);
  const [actionResult, setActionResult] = useState<ActionResultState | null>(null);

  const filterDefinitions = useMemo(() => buildFilterDefinitions(loans), [loans]);

  // -------------------------------------------------------------------------
  // Filtered data based on search and filters
  // -------------------------------------------------------------------------

  const filteredLoans = useMemo(() => {
    let result = [...loans];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(loan =>
        loan.id.toLowerCase().includes(query) ||
        loan.borrower.toLowerCase().includes(query) ||
        loan.collateralType.toLowerCase().includes(query) ||
        collateralLabel(loan.collateralType).toLowerCase().includes(query)
      );
    }

    for (const filter of activeFilters) {
      switch (filter.filterId) {
        case 'status':
          result = result.filter(loan => loan.status === filter.value);
          break;
        case 'collateralType':
          result = result.filter(loan => loan.collateralType === filter.value);
          break;
        case 'borrower':
          result = result.filter(loan =>
            loan.borrower.toLowerCase().includes(filter.value.toLowerCase())
          );
          break;
        // The Maturity Date filter was offered but had no case here, so an
        // applied range left every row in place (r3-fi-g7-6).
        case 'maturityDate':
          result = result.filter(loan => maturityInRange(loan.maturityDate, filter.value));
          break;
      }
    }

    return result;
  }, [loans, searchQuery, activeFilters]);

  // -------------------------------------------------------------------------
  // Bulk action handlers
  // -------------------------------------------------------------------------

  /**
   * Sends the selected loans' record ids (LoanViewItem.loanId, the UUID the
   * spec's BulkLoanActionRequest takes), waits for the result, reports the
   * count the server changed, and refetches the grid. The display `id` is the
   * loan number, which the server never matches.
   */
  const runBulkAction = useCallback(async (kind: 'approve' | 'close') => {
    const ids = selectedRows
      .map((row) => row.loanId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    const unsent = selectedRows.length - ids.length;
    const unsentNote = unsent > 0
      ? ` ${plural(unsent, 'selected row')} had no record id and ${unsent === 1 ? 'was' : 'were'} not sent.`
      : '';

    if (ids.length === 0) {
      setActionResult({
        type: 'error',
        message: `Nothing was sent: the selected loans carry no record id.`,
      });
      return;
    }

    setActionPending(true);
    setActionResult(null);
    try {
      // null only when the server answers 204 (apiClient.request).
      const result: BulkLoanActionResponse | null = kind === 'approve'
        ? await loansApi.approve(ids)
        : await loansApi.archive(ids);
      // `updated` and `archived` are optional in the spec: a response without
      // the one this action reports is said so, never read as 0.
      const count = kind === 'approve' ? result?.updated : result?.archived;
      const verb = kind === 'approve' ? 'Approved' : 'Closed';

      if (count == null) {
        setActionResult({
          type: 'warning',
          message: `The ${kind} request completed, but the response did not report how many loans changed.${unsentNote}`,
        });
      } else {
        const partial = count < ids.length;
        const approveNote = kind === 'approve' && partial
          ? ' Only submitted or underwriting loans can be approved.'
          : '';
        setActionResult({
          type: partial || unsent > 0 ? 'warning' : 'success',
          message: `${verb} ${count} of ${plural(ids.length, 'selected loan')}.${approveNote}${unsentNote}`,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ACTIVE_LOANS_KEY });
    } catch (err) {
      // A 409 from Close loan means the server refused the request: some
      // selected loan may not close, so none closed (ruling
      // loan-status-buckets-archive-guard (a)). Each refused loan is listed
      // with its reason, and the grid refetches, since a refused loan's
      // status may have changed since the grid loaded.
      const refused = kind === 'close' ? refusedLoansOf(err) : null;
      if (refused) {
        setActionResult({
          type: 'error',
          message: `No loans were closed: ${plural(refused.length, 'selected loan')} may not close.${unsentNote}`,
          refused: refusalLines(refused, selectedRows),
        });
        await queryClient.invalidateQueries({ queryKey: ACTIVE_LOANS_KEY });
        return;
      }
      setActionResult({
        type: 'error',
        message: readErrorCopy(err, kind === 'approve' ? APPROVE_FAILED_COPY : CLOSE_FAILED_COPY),
      });
    } finally {
      setActionPending(false);
    }
  }, [selectedRows, queryClient]);

  // Both actions change a loan's status, so both ask first and name the
  // transition (r3-fi-g7-5). The second action sets the selected loans to
  // CLOSED (loansApi.archive -> archiveLoans): it read 'Archive' behind a
  // trash-can icon and a generic 'Archive 1 item?', which reads as removing a
  // row rather than closing a loan. Its question names the loans that may
  // close; the server refuses the rest, all or nothing.
  const bulkActions: ActionDefinition[] = useMemo(() => [
    {
      id: 'approve',
      label: 'Approve',
      icon: CheckIcon,
      variant: 'primary',
      disabled: actionPending,
      confirmMessage: (n: number) => `Approve ${plural(n, 'loan')}?`,
      onClick: () => { void runBulkAction('approve'); },
    },
    {
      id: 'close',
      label: 'Close loan',
      icon: ArchiveIcon,
      variant: 'danger',
      disabled: actionPending,
      confirmMessage: (n: number) => `Set ${plural(n, 'loan')} to Closed? ${CLOSE_RULE}`,
      onClick: () => { void runBulkAction('close'); },
    },
  ], [actionPending, runBulkAction]);

  // -------------------------------------------------------------------------
  // Summary metrics
  // -------------------------------------------------------------------------

  // The grid carries applications and approved loans in review beside funded
  // loans. The exposure covers the funded rows only (Active and Alert), so an
  // application in underwriting or an approved, unfunded loan never reads as
  // exposure (r3-fi-g7-7; ruling loan-status-buckets-archive-guard (a) moved
  // APPROVED to Review). The first card carries the Active bucket's label,
  // 'Active and funded', so it counts that bucket and names the rows it leaves
  // out (ruling active-loans-tile-vs-bucket (a)); it counted Alert rows too
  // under the label 'Active Loans', which the Dashboard tile uses for ACTIVE
  // alone.
  const fundedLoans = useMemo(
    () => filteredLoans.filter((loan) => loan.status === 'Active' || loan.status === 'Alert'),
    [filteredLoans],
  );
  const activeAndFundedCount = useMemo(
    () => filteredLoans.filter((loan) => loan.status === 'Active').length,
    [filteredLoans],
  );
  const alertCount = useMemo(
    () => filteredLoans.filter((loan) => loan.status === 'Alert').length,
    [filteredLoans],
  );
  const totalExposure = useMemo(
    () => fundedLoans.reduce((sum, loan) => sum + loan.loanAmount, 0),
    [fundedLoans],
  );

  // A mean over zero loans is not a measurement; the card says so instead of 0%.
  // A loan whose row omits ltv is left out of the mean.
  const reportedLtvs = useMemo(
    () =>
      filteredLoans.flatMap((loan) =>
        typeof loan.ltv === 'number' && Number.isFinite(loan.ltv) ? [loan.ltv] : [],
      ),
    [filteredLoans],
  );
  const averageLtv = reportedLtvs.length > 0
    ? Math.round(reportedLtvs.reduce((sum, ltv) => sum + ltv, 0) / reportedLtvs.length)
    : null;

  const reviewCount = useMemo(
    () => filteredLoans.filter(l => l.status === 'Review').length,
    [filteredLoans],
  );
  // The Collateral Health Alerts card counts loans by CHA tier, the
  // institution's own policy verdict, and never by loan status (ruling
  // ltv-threshold-arbiter): a loan whose worst open alert is Yellow, Orange or
  // Red is counted, and Red is named. A loan with no tier has no open
  // collateral alert and is not counted.
  const openChaAlerts = useMemo(() => {
    let atRisk = 0;
    let atRed = 0;
    for (const loan of filteredLoans) {
      if (loan.tier === 'YELLOW' || loan.tier === 'ORANGE' || loan.tier === 'RED') atRisk += 1;
      if (loan.tier === 'RED') atRed += 1;
    }
    return { atRisk, atRed };
  }, [filteredLoans]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant H1. Export is the
            grid toolbar's; this header carries no disabled placeholders. */}
        <p className="text-sm font-sans text-tertiary">Monitor and manage active loan positions</p>
      </div>

      {/* Error State */}
      {error && (
        <Alert type="error" className="mb-6">
          {readErrorCopy(error, LOAD_FAILED_COPY)}
        </Alert>
      )}

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))
        ) : (
          <>
            <MetricCard
              label={STATUS_LABELS.Active}
              value={String(activeAndFundedCount)}
              description={`Not counted: ${reviewCount} in review or approved, ${alertCount} suspended or defaulted`}
            />
            <MetricCard
              label="Total Exposure"
              value={compactUsd.format(totalExposure)}
              description="Sum of active, funded, suspended and defaulted loan amounts"
            />
            <MetricCard
              label="Average LTV"
              value={averageLtv == null ? '--' : `${averageLtv}%`}
              caption={averageLtv == null ? 'No loans with an LTV' : undefined}
              description={averageLtv == null ? undefined : "Simple mean of each loan's LTV"}
            />
            <MetricCard
              label="Collateral Health Alerts"
              value={String(openChaAlerts.atRisk)}
              description={`Loans open at Yellow, Orange or Red; ${openChaAlerts.atRed} at Red`}
            />
          </>
        )}
      </div>

      {actionResult && (
        <Alert type={actionResult.type} className="mb-4">
          {actionResult.message}
          {actionResult.refused && (
            <ul className="mt-2 list-disc pl-5 space-y-1">
              {actionResult.refused.map((line) => (
                <li key={line.key}>{line.text}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {/* Filter Bar */}
      <FilterBar
        filters={filterDefinitions}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search loans by ID, borrower, or collateral..."
        className="rounded-t-sm"
      />

      {/* Action Bar (visible when rows selected) */}
      <ActionBar
        selectedCount={selectedRows.length}
        totalCount={filteredLoans.length}
        actions={bulkActions}
        onSelectAll={() => setSelectedRows([...filteredLoans])}
        onDeselectAll={() => setSelectedRows([])}
      />

      {/* Data Grid */}
      {isLoading ? (
        <div className="bg-surface rounded-b-sm shadow-sm p-8">
          <Skeleton className="h-8 w-full mb-4" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<Loan>
          data={filteredLoans}
          columns={COLUMNS}
          enableRowSelection
          enableMultiRowSelection
          onSelectionChange={setSelectedRows}
          enablePagination
          defaultPageSize={25}
          enableExport
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => row.id}
          className="rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
